import React from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Calculates weld buffers for multiple components
 * @param components      Array of components to process weld information from
 * @param bufferSize      The distance around welds to use as a buffer
 * @param edgeDistanceThreshold The minimum distance from component edges to include welds (in inches)
 * @returns Array of component rects surrounding weld lines
 */
export const getWeldBuffersForComponents = (
  components: ReadonlyArray<GeckoComponentModel>,
  bufferSize?: GeckoMeasurement,
  edgeDistanceThreshold?: GeckoMeasurement
): Map<string, GeckoComponentRect[]> => {
  const result = new Map<string, GeckoComponentRect[]>()
  const edgeThreshold =
    edgeDistanceThreshold ??
    GeckoMeasurement.CreateFromValue(6, GeckoMeasurementUnit.Inches)

  components.forEach((component) => {
    const currComponentWeldBuffers = new Array<GeckoComponentRect>()
    result.set(component.id, currComponentWeldBuffers)
    if (
      component.type === GeckoComponentModelType.VerticalTankShell ||
      component.type === GeckoComponentModelType.TankFloor ||
      component.type === GeckoComponentModelType.TankFlatRoof
    ) {
      component.horizontalWelds?.forEach((weld) => {
        const weldRect = weld.feature_geometry
        // Ignore welds at the bottom of the asset
        if (
          weldRect.y.min
            .minus(component.componentBounds.y.min)
            .abs()
            .lt(edgeThreshold)
        ) {
          return
        }
        // Ignore welds at the top of the asset
        if (
          weldRect.y.max
            .minus(component.componentBounds.y.max)
            .abs()
            .lt(edgeThreshold)
        ) {
          return
        }

        currComponentWeldBuffers.push(
          GeckoComponentRect.CreateFromCoords(
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.start.x,
              weldRect.start.y.minus(bufferSize)
            ),
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.end.x,
              weldRect.end.y.plus(bufferSize)
            )
          )
        )
      })
      component.verticalWelds.forEach((weld) => {
        const weldRect = weld.feature_geometry
        currComponentWeldBuffers.push(
          GeckoComponentRect.CreateFromCoords(
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.start.x.minus(bufferSize),
              weldRect.start.y
            ),
            GeckoComponentCoords.CreateFromMeasurements(
              weldRect.componentID,
              weldRect.end.x.plus(bufferSize),
              weldRect.end.y
            )
          )
        )
      })
    }
  })

  return result
}

/**
 * Returns an array of component rects surrounding weld lines provided from
 * either a rich asset model's component or the component entry itself
 * @param component       The component to pull weld information from
 * @param bufferSize      The distance around welds to use as a buffer
 */
export const useWeldBuffers = (
  component: GeckoComponentModel | undefined,
  bufferSize?: GeckoMeasurement
): ReadonlyArray<GeckoComponentRect> =>
  React.useMemo(() => {
    if (!component) {
      return []
    }
    const weldBuffersMap = getWeldBuffersForComponents([component], bufferSize)
    return weldBuffersMap.get(component.id) ?? []
  }, [bufferSize, component])
